import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// Phase 4 of docs/build-plans/pma-stripe-payments.md.
//
// Parent-portal payment intent creation. Calls the Phase 2 server route
// POST /api/portal/invoices/:id/payment-intent — parent-JWT gated, scoped
// to the parent's own invoices.
//
// The publishable key flows through the response (not a build-time env
// var) so a future platform-key rotation doesn't need a pma-client
// rebuild. See Phase 2 architectural decisions.

export interface PaymentIntentResponse {
    /** Stripe PaymentIntent client_secret. Pass to <Elements options={{clientSecret}}>. */
    clientSecret: string;
    /** Platform publishable key (pk_test_… / pk_live_…). Pass to loadStripe(). */
    publishableKey: string;
    /** Connected-account id (acct_…). Must be passed to loadStripe()'s
     *  `stripeAccount` option so Elements queries the PaymentIntent on
     *  the right account — Phase 2 mints intents on the practice's
     *  Connect account, not on the platform account. Without it,
     *  Stripe.js's elements/sessions lookup 400s. */
    stripeAccount: string;
    /** Practice name to override Stripe's mandate-text merchant display.
     *  Without this PaymentElement falls back to the connected account's
     *  business profile, which in test mode reads the placeholder name
     *  from fake onboarding ("asdf asdf"). Pass to PaymentElement
     *  `options.business.name`. */
    businessName: string | null;
    /** True when the server reused an in-flight PaymentIntent (Phase 2 idempotency). */
    reused?: boolean;
}

interface RawResponse {
    client_secret: string;
    publishable_key: string;
    stripe_account: string;
    business_name?: string | null;
    reused?: boolean;
}

export function useCreatePaymentIntent(invoiceId: string | undefined) {
    return useMutation({
        mutationFn: async (): Promise<PaymentIntentResponse> => {
            if (!invoiceId) throw new Error('invoiceId is required');
            const res = await api.post<RawResponse>(
                `/portal/invoices/${invoiceId}/payment-intent`,
            );
            const { client_secret, publishable_key, stripe_account, reused } = res.data;
            if (!client_secret || !publishable_key || !stripe_account) {
                throw new Error('Server did not return a payable PaymentIntent.');
            }
            return {
                clientSecret:   client_secret,
                publishableKey: publishable_key,
                stripeAccount:  stripe_account,
                businessName:   res.data.business_name ?? null,
                reused,
            };
        },
    });
}

// ── Phase 8 — saved payment methods + autopay ──────────────────────────

export interface PortalPaymentMethod {
    id: string;
    type: 'card' | 'us_bank_account' | string;
    last4: string | null;
    brand: string | null;
    expMonth: number | null;
    expYear: number | null;
    isDefault: boolean;
    /** Phase 8 follow-up — true when the card was saved with the
     *  autopay box ticked (off_session mandate). False means save-only;
     *  the autopay PATCH refuses to enable autopay against this card. */
    autopayConsent: boolean;
    createdAt: string | null;
}

interface RawPortalPaymentMethod {
    id: string;
    type: string;
    last4: string | null;
    brand: string | null;
    expMonth: number | null;
    expYear: number | null;
    isDefault: boolean;
    autopayConsent?: boolean;
    createdAt: string | null;
}

/** GET /api/portal/payment-methods — saved methods, default first. */
export function useSavedPaymentMethods(enabled = true) {
    return useQuery({
        queryKey: ['portal', 'payment-methods'] as const,
        queryFn: async (): Promise<PortalPaymentMethod[]> => {
            const res = await api.get<RawPortalPaymentMethod[]>('/portal/payment-methods');
            // Normalize the optional autopayConsent — older server
            // responses may omit the field; treat absent as false.
            return res.data.map((r) => ({
                ...r,
                autopayConsent: r.autopayConsent === true,
            }));
        },
        enabled,
        staleTime: 30_000,
    });
}

/** POST /api/portal/setup-intent — for the AddCardDialog "save without
 *  paying" flow. Accepts forAutopay: when true, the SetupIntent uses
 *  off_session (mandate-signed, autopay-eligible); when false, on_session
 *  (save-only, no mandate). */
export function useCreateSetupIntent() {
    return useMutation({
        mutationFn: async (forAutopay: boolean = false): Promise<PaymentIntentResponse> => {
            const res = await api.post<RawResponse>(
                '/portal/setup-intent',
                { for_autopay: forAutopay },
            );
            const { client_secret, publishable_key, stripe_account, reused } = res.data;
            if (!client_secret || !publishable_key || !stripe_account) {
                throw new Error('Server did not return a usable SetupIntent.');
            }
            return {
                clientSecret:   client_secret,
                publishableKey: publishable_key,
                stripeAccount:  stripe_account,
                businessName:   res.data.business_name ?? null,
                reused,
            };
        },
    });
}

/** PATCH /api/portal/payment-methods/:id/default — atomic default flip. */
export function useSetDefaultPaymentMethod() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string): Promise<{ ok: true }> => {
            const res = await api.patch<{ ok: true; isDefault: boolean }>(
                `/portal/payment-methods/${id}/default`,
            );
            return { ok: res.data.ok };
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['portal', 'payment-methods'] });
        },
    });
}

/** DELETE /api/portal/payment-methods/:id — Stripe-detach + row delete.
 *  Returns 409 + { affectedInvoices: [{id, displayId}] } when deleting
 *  the only saved method while an autopay invoice exists; the dialog
 *  surfaces those so the parent can disable autopay first. */
export function useDeletePaymentMethod() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string): Promise<{ ok: true }> => {
            const res = await api.delete<{ ok: true }>(`/portal/payment-methods/${id}`);
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['portal', 'payment-methods'] });
            // Invoice detail caches autopay state — refresh so a delete
            // that cascaded a default-promotion is reflected in the
            // saved-card pay button.
            qc.invalidateQueries({ queryKey: ['portal'] });
        },
    });
}

/** POST /api/portal/invoices/:id/payment-intent/:pi/save-future-usage —
 *  flips setup_future_usage on an in-flight PaymentIntent so the
 *  parent can toggle save+autopay choices without remounting Elements.
 *  Server 409s if the intent has already succeeded.
 *
 *  Tri-state: 'off' (don't save), 'save_only' (on_session — save but
 *  no autopay), 'save_and_autopay' (off_session — save + mandate-
 *  signed). The two-checkbox UI maps:
 *    save unchecked                → 'off'
 *    save checked, autopay off     → 'save_only'
 *    save checked, autopay on      → 'save_and_autopay' */
export type SaveFutureUsageMode = 'off' | 'save_only' | 'save_and_autopay';

export function useUpdatePaymentIntentSaveFutureUsage(invoiceId: string, paymentIntentId: string | null) {
    return useMutation({
        mutationFn: async (mode: SaveFutureUsageMode): Promise<{ ok: true }> => {
            if (!paymentIntentId) throw new Error('paymentIntentId is required');
            const res = await api.post<{ ok: true }>(
                `/portal/invoices/${invoiceId}/payment-intent/${paymentIntentId}/save-future-usage`,
                { mode },
            );
            return res.data;
        },
    });
}

/** PATCH /api/portal/invoices/:id/autopay — toggle on/off. Server 409s
 *  when enabling without a saved default card. */
export function useSetInvoiceAutopay() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ invoiceId, enabled }: { invoiceId: string; enabled: boolean }) => {
            const res = await api.patch<{ ok: true; autopayEnabled: boolean }>(
                `/portal/invoices/${invoiceId}/autopay`,
                { enabled },
            );
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['portal'] });
        },
    });
}
